// Components==============
import ControlBar from 'components/Layout/ControlBar/ControlBar';
import ExerciseBottom from 'components/UI/ExerciseBottom/ExerciseBottom';
import ExerciseWrapper from 'components/UI/ExerciseWrapper/ExerciseWrapper';
import React, { createContext, useContext, useEffect, useState } from 'react';
import updateExerciseStatus from 'actions/authentication/updateExerciseStatus';
import { useDispatch, useSelector } from 'react-redux';
import { HomeworkContext } from 'screens/Homework/Homework';
import { useCombineAndShuffleArrays } from 'utilities/useCombineAndShuffleArrays';
import { useHomeworkNavigation } from 'utilities/useHomeworkNavigation';
import Category from './components/Category/Category';
import './MultipleChoiceExercise.module.scss';
// =========================

type UserAnswerType = {
    answer: string;
    word: string;
};

type MultipleChoiceExerciseContextTypes = {
    useWords: 0 | 1;
    useImages: 0 | 1;
    exerciseStatus: exerciseStatusType;
    setExerciseStatus: React.Dispatch<React.SetStateAction<exerciseStatusType>>;
    userAnswers: UserAnswerType[];
    setUserAnswers: React.Dispatch<React.SetStateAction<UserAnswerType[]>>;
    current: number;
    setCurrent: React.Dispatch<React.SetStateAction<number>>;
    words: Words[];
    answer: Words;
};

export const MultipleChoiceExerciseContext = createContext(
    {} as MultipleChoiceExerciseContextTypes
);

export default function MultipleChoiceExercise({
    exercise,
    setStatus,
}: {
    exercise: ExerciseEntity;
    setStatus: React.Dispatch<React.SetStateAction<exerciseStatusType>>;
}) {
    const categories = exercise?.categories;
    const useImages = exercise?.use_images;
    const useWords = exercise?.use_words;

    const dispatch = useDispatch();

    const { setProgress } = useContext(HomeworkContext);

    const [userAnswers, setUserAnswers] = useState<UserAnswerType[]>([]);
    const [exerciseStatus, setExerciseStatus] = useState<exerciseStatusType>(
        'started'
    );
    const { next, current, setCurrent } = useHomeworkNavigation(userAnswers);

    const [words, setWords] = useState<Words[]>([]);

    const wordsGetter: Words[] = useCombineAndShuffleArrays(
        categories
    ).sort((a, b) => (a?.type > b?.type ? 1 : 0));

    useEffect(() => {
        setWords(wordsGetter);
    }, [wordsGetter.length]);

    const [answer, setAnswer] = useState(words[current]);

    useEffect(() => {
        if (words.length === 0) return;
        if (userAnswers.length === words.length) setExerciseStatus('finished');
    }, [userAnswers]);

    useEffect(() => setAnswer(words[current]), [words, current]);

    useEffect(() => setStatus(exerciseStatus), [exerciseStatus]);

    const givenAnswers = useSelector((state: ReduxStore) =>
        state?.authentication?.user?.exercise_status?.find(
            (e) =>
                e.exercise_id === exercise?.id &&
                e.exercise_type === 'App\\Models\\Exercise'
        )
    )?.metadata as UserAnswerType[] | undefined;

    useEffect(() => {
        if (exerciseStatus === 'started' && givenAnswers) {
            //  get all words except the answered ones
            const filteredWords = words?.filter(
                (w) => !givenAnswers.find((a) => a.word === w.name)
            );

            //  get all answered words
            const answeredWords = givenAnswers
                .map((a) => words.find((w) => w.name === a.word))
                .filter((exists) => exists) as Words[];

            //  put answered words before filtered words to only get unanswered words
            setWords([...answeredWords, ...filteredWords]);
            setUserAnswers(givenAnswers);
            setCurrent(givenAnswers.length);
        }
    }, [exerciseStatus, words.length]);

    useEffect(() => {
        setProgress(userAnswers?.length);

        if (givenAnswers && givenAnswers.length >= userAnswers.length) return;

        // save user answer
        if (userAnswers[current])
            dispatch(
                updateExerciseStatus({
                    exercise_id: exercise?.id,
                    exercise_type: 'homework',
                    finished: 0,
                    newWord: userAnswers[current],
                })
            );
    }, [userAnswers]);

    return (
        <MultipleChoiceExerciseContext.Provider
            value={{
                useImages,
                useWords,
                exerciseStatus,
                setExerciseStatus,
                userAnswers,
                setUserAnswers,
                current,
                setCurrent,
                words,
                answer,
            }}
        >
            <ExerciseWrapper>
                <div styleName="wrapper">
                    {categories.map((c) => (
                        <Category key={c.id} category={c} />
                    ))}
                </div>
            </ExerciseWrapper>
            <ExerciseBottom
                controlBar={
                    <ControlBar
                        exerciseStatus={exerciseStatus}
                        next={next}
                        phoneme={answer?.phoneme}
                        audioToBePlayed={answer?.audio}
                        textToBeRead={answer?.name}
                    />
                }
            />
        </MultipleChoiceExerciseContext.Provider>
    );
}
